import styled from '@emotion/styled';
import { SpeakingSessionRoomStructureDto, SpeakingSlotStatus } from 'api/planning';
import { useMemo } from 'react';
import { formatTimeOnly } from 'utils';
import { RoomClashPeriodDto } from './utils/calculateClashedPeriods';
import { normalizeEndTime } from './utils/normalizeEndTime';
import { NUMBER_OF_SLOTS, NUMBER_OF_SLOTS_PER_HOUR, SLOT_TIME_MINUTES } from './speakingSlotTime';

type Props = {
  localSpeakingSlotMinutesOffset: number;
  structures: SpeakingSessionRoomStructureDto[];
  clashedPeriods: RoomClashPeriodDto[];
};

export const RoomAllocationPreview = ({
  structures,
  localSpeakingSlotMinutesOffset,
  clashedPeriods,
}: Props) => {
  const slotStartTimes = Array.from({ length: NUMBER_OF_SLOTS }, (_, index) => {
    const totalStartMinutes =
      (index * SLOT_TIME_MINUTES + localSpeakingSlotMinutesOffset) % (24 * 60);
    return formatTimeOnly(Math.floor(totalStartMinutes / 60), totalStartMinutes % 60);
  });

  const clashedSlotStartTimes = useMemo(() => {
    const clashedTimes = new Set<string>();

    slotStartTimes.forEach((slotStartTime) => {
      const isClashed = clashedPeriods.some((period) => {
        const periodStart = period.startTime;
        const periodEnd = normalizeEndTime(period.endTime);
        const normalizedSlotTime = slotStartTime;

        return periodStart <= normalizedSlotTime && normalizedSlotTime < periodEnd;
      });

      if (isClashed) {
        clashedTimes.add(slotStartTime);
      }
    });

    return clashedTimes;
  }, [slotStartTimes, clashedPeriods]);

  const occupiedSlots = structures
    .flatMap((x) => x.speakingSlots)
    .reduce((acc: { [key: string]: SpeakingSlotStatus }, slot) => {
      if (acc[slot.startTime] == undefined || acc[slot.startTime] === SpeakingSlotStatus.Break) {
        acc[slot.startTime] = slot.status;
      }

      return acc;
    }, {});

  const cells = slotStartTimes.map((x) => {
    const isClashed = clashedSlotStartTimes.has(x);
    return isClashed ? (
      <S.ClashedSlot key={x} />
    ) : (
      <S.Slot key={x} className={SpeakingSlotStatus[occupiedSlots[x]]} />
    );
  });

  return (
    <S.Grid numberOfSlots={NUMBER_OF_SLOTS} numberOfSlotsPerHour={NUMBER_OF_SLOTS_PER_HOUR}>
      {cells}
    </S.Grid>
  );
};

const S = {
  Grid: styled.div<{ numberOfSlots: number; numberOfSlotsPerHour: number }>`
    user-select: none;
    align-items: center;
    display: grid;
    grid-template-columns: repeat(
      ${(props) => props.numberOfSlots},
      minmax(calc(90px / ${(props) => props.numberOfSlotsPerHour}), 1fr)
    );
  `,
  ClashedSlot: styled.div`
    background-color: #d5d8dd;
    height: 100%;
    opacity: 0.5;
  `,
  Slot: styled.div`
    height: 10px;

    &.Available,
    &.AwaitingApproval,
    &.Booked,
    &.Break,
    &.Rejected {
      color: #e2e2e3;
      border-left: 1px solid #e2e2e3;
    }
    &.Available {
      background-color: #21ba45;
    }
    &.AwaitingApproval {
      background-color: #fbbd08;
    }
    &.Booked {
      background-color: #bf2020;
    }
    &.Break {
      background-color: #343637;
    }
    &.Rejected {
      background-color: #f3f4f5;
    }
  `,
};
